import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Title,
  Filler,
} from "chart.js";
import "chartjs-adapter-date-fns";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Title,
  Filler
);

const TimeSeriesChart = ({ data, deviceName }) => {
  const chartRefs = useRef({}); 

 
const COLORS = [
  "#2196F3",
  "#4CAF50",
  "#FF0807",
  "#FFC107",
  "#FF4560",
  "#29A2CC",
];

const getColor = (index) => COLORS[index % COLORS.length];


 const legendSpacingPlugin = {
  id: "legendSpacing",
  beforeInit(chart) {
    const originalFit = chart.legend.fit;
    chart.legend.fit = function fit() {
      originalFit.bind(chart.legend)();
      this.height += 10;  
    };
  },
};




  // Jin devices ke telemetry key me sensor ka naam seedha hota hai (…_A, …_B1)
  // unka deviceSensorMap me entry nahi hai, isliye expected sensor yahan se
  // aate hain: [sensor, elevation]. Elevation sirf water-level key ke
  // "(Sensor Elevation = …)" hisse ke liye chahiye.
  const EXPECTED_SENSORS = {
    "VWP-WP-25": [["A", "+31.0"], ["B1", "-17.0"], ["B", "-28.5"]],
    "VWP-WP-26": [["A", "+31.5"], ["B1", "-17.0"]],
    "VWP-WP-27": [["A", "+31.5"], ["B1", "-17.0"]],
    "VWP-WP-28": [["A", "+29.5"], ["B1", "-17.0"]],
    "VWP-WP-29": [["A", "+35.0"], ["B1", "-20.0"]],
    "VWP-WP-30": [["A", "+31.5"], ["B1", "-21.7"]],
  };

  const WATER_TITLE = "Piezometer Water level Elevation (In Feet)";
  const PRESSURE_TITLE = "Pressure In KPa";

  const isPiezoDevice = () => /^VWP-WP-/i.test(String(deviceName ?? ""));

  const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Sensor token key ke end me ya uske baad non-alphanumeric par khatam hota
  // hai — warna "B" pattern "B1" wali key ko bhi match kar leta.
  const sensorTokenRe = (name) => new RegExp(`_${escapeRe(name)}(?![A-Za-z0-9])`);

  /**
   * Group me jis sensor ki koi reading nahi aayi, uske liye khali series daal
   * deta hai taaki legend me uska label phir bhi dikhe.
   *
   * Key ka format hardcode karne ke bajaye us group ki kisi maujood key se
   * copy hota hai — sirf sensor token (aur water-level key ka elevation
   * number) badla jaata hai. Isse prefix/casing wahi rehti hai jo asli data me
   * hai, chahe wo "water_level_elevation_S1" ho ya
   * "Water_Level_Elevation_B1 (Sensor Elevation = -17.0)".
   */
  const fillMissingSensors = (group, sensors) => {
    const existing = Object.keys(group);
    if (!existing.length || !sensors.length) return;

    // Lambe token pehle, taaki "B1" wali key "B" se match na ho jaye.
    const names = sensors.map(([name]) => name).sort((a, b) => b.length - a.length);

    let template = null;
    for (const key of existing) {
      const token = names.find((name) => sensorTokenRe(name).test(key));
      if (token) {
        template = { key, token };
        break;
      }
    }
    if (!template) return;

    const at = template.key.search(sensorTokenRe(template.token));
    const prefix = template.key.slice(0, at + 1); // "_" samet
    const tail = template.key.slice(at + 1 + template.token.length);

    sensors.forEach(([name, elevation]) => {
      // Maujoodgi key se nahi, sensor token se check hoti hai — warna agar
      // asli key ki formatting yahan bani key se zara bhi alag ho to ek hi
      // sensor ki do series (ek bhari, ek khali) ban jaati.
      if (existing.some((key) => sensorTokenRe(name).test(key))) return;

      group[
        prefix +
          name +
          (elevation ? tail.replace(/[+-]?\d+(?:\.\d+)?/, elevation) : tail)
      ] = [];
    });
  };

  // Device ke saare sensor kaun se hain. deviceSensorMap wale devices ki key
  // "…_S1" shakl ki hoti hai; EXPECTED_SENSORS wale devices me key hi label
  // hai, isliye unka poora naam banana padta hai.
  const seedGroup = (group, kind) => {
    const sensors = deviceSensorMap[deviceName]?.water_level;

    if (sensors) {
      Object.keys(sensors).forEach((s) => {
        group[kind === "pressure" ? `pressure_in_kPa_${s}` : `water_level_elevation_${s}`] = [];
      });
    } else if (EXPECTED_SENSORS[deviceName]) {
      EXPECTED_SENSORS[deviceName].forEach(([name, elevation]) => {
        group[
          kind === "pressure"
            ? `pressure_in_kPa_${name}`
            : `Water_Level_Elevation_${name} (Sensor Elevation = ${elevation})`
        ] = [];
      });
    }
    // Jis device ke sensor kahin list nahi hain uska chart title ke saath khali
    // aata hai — legend ke liye sensor naam ka koi source nahi hai.
  };

  /**
   * Piezo device par Pressure aur Water level dono chart hamesha bante hain.
   *
   * Reading na aane ke do alag raaste hain aur dono se guzarna padta hai:
   * data hi khali ho, ya rows aayein par sab filter ho jaayein (value "NaN")
   * — dusre case me upar wala loop kuch group hi nahi banata tha, isliye
   * `.graph` khali reh jaata aur DOCX me graph ki jagah patli si lakeer aati.
   */
  const ensurePiezoGroups = (keyGroup) => {
    if (!isPiezoDevice()) return;

    [
      [PRESSURE_TITLE, "pressure"],
      [WATER_TITLE, "water"],
    ].forEach(([title, kind]) => {
      if (!keyGroup[title]) keyGroup[title] = {};

      if (Object.keys(keyGroup[title]).length === 0) {
        seedGroup(keyGroup[title], kind);
      } else {
        // Group me kuch sensor hain, kuch nahi — sirf missing wale jodte hain.
        const expected =
          EXPECTED_SENSORS[deviceName] ||
          (deviceSensorMap[deviceName]
            ? Object.keys(deviceSensorMap[deviceName].water_level).map((s) => [s, null])
            : null);
        if (expected) fillMissingSensors(keyGroup[title], expected);
      }
    });
  };

  const groupDataForCharts = () => {
    const keyGroup = {};

      if (!data || data.length === 0) {
    const startDate = new Date();
    startDate.setDate(1);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(0);
    endDate.setHours(23, 59, 59, 999);
 
    // Data na hone par bhi dono chart, unke title aur sensor legend dikhne
    // chahiye — sirf line khali rehti hai.
    ensurePiezoGroups(keyGroup);

    if (Object.keys(keyGroup).length === 0) {
      keyGroup[""] = {
        empty: [],
      };
    }

    return { keyGroup, startDate, endDate };
  }


    // if (!data || data.length === 0)
    //   return { keyGroup, startDate: null, endDate: null };

    let minTs = Math.min(...data.map((d) => Number(d.ts)));
    let startDate = new Date(minTs);
    startDate.setDate(1);
    startDate.setHours(0, 0, 0, 0);

    let endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(0);
    endDate.setHours(23, 59, 59, 999);

    data.forEach((item) => {
      const timestamp = Number(item.ts);
      if (item.value === "NaN") return;

      const value =
        item.value == null || isNaN(parseFloat(item.value))
          ? null
          : parseFloat(item.value);

      let keyname = item.key;
      const lastChar = keyname.charAt(keyname.length - 1);
      if (!isNaN(lastChar)) keyname = keyname.slice(0, -2);
 


  if(deviceName=='VWP-WP-25' || deviceName=='VWP-WP-27' || deviceName=='VWP-WP-29' || deviceName=='VWP-WP-28' || deviceName=='VWP-WP-26' || deviceName=='VWP-WP-30') 
 {
if(keyname.startsWith('pressure_in_kPa_'))
{
    const groupTitle = 'Pressure In KPa';
     if (!keyGroup[groupTitle]) keyGroup[groupTitle] = {};
      if (!keyGroup[groupTitle][item.key]) keyGroup[groupTitle][item.key] = [];

      keyGroup[groupTitle][item.key].push([timestamp, value]);

}
 
if(keyname.startsWith('Water_Level_Elevation'))
{
    const groupTitle = 'Piezometer Water level Elevation (In Feet)';
     if (!keyGroup[groupTitle]) keyGroup[groupTitle] = {};
      if (!keyGroup[groupTitle][item.key]) keyGroup[groupTitle][item.key] = [];

      keyGroup[groupTitle][item.key].push([timestamp, value]);

}}
else{
      if (keyname === "water_level_elevation_")
        keyname = "Piezometer Water level Elevation (In Feet)";
      if (keyname === "pressure_in_kPa_") keyname = "Pressure In KPa";

      if (!keyGroup[keyname]) keyGroup[keyname] = {};
      if (!keyGroup[keyname][item.key]) keyGroup[keyname][item.key] = [];

      keyGroup[keyname][item.key].push({ x: timestamp, y: value });
}
    });

    // Jis sensor ki is mahine koi reading nahi aayi wo upar ke loop me group
    // hi nahi hota, isliye uska label bhi gayab ho jaata tha. Yahan uski khali
    // series daal dete hain — line nahi banti, sirf legend me label aa jaata
    // hai.
    //
    // Rows aa gayin par shayad sab filter ho gayin, ya kisi ek sensor ki hi
    // aayin — dono soorat me baaki sensor ki khali series yahin judti hai.
    ensurePiezoGroups(keyGroup);

    return { keyGroup, startDate, endDate };
  };

 

  const deviceSensorMap = {
  "VWP-WP-01": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B1 (Sensor Elevation = -17.0)",
      S3: "Water_Level_Elevation_B (Sensor Elevation = -28.5)",
      S4: "Water_Level_Elevation_C (Sensor Elevation = -63.0)",
      S5: "Water_Level_Elevation_D (Sensor Elevation = -80.0)",
    },
  
  },

  "VWP-WP-02": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B (Sensor Elevation = -28.5)",
      S3: "Water_Level_Elevation_C (Sensor Elevation = -63.0)",
      S4: "Water_Level_Elevation_D (Sensor Elevation = -77.0)",
      S5: "Water_Level_Elevation_D (Sensor Elevation = -77.0)",
    },
  },

  "VWP-WP-03": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B1 (Sensor Elevation = -17.0)",
      S3: "Water_Level_Elevation_B (Sensor Elevation = -28.5)",
      S4: "Water_Level_Elevation_C (Sensor Elevation = -63.0)",
      S5: "Water_Level_Elevation_D (Sensor Elevation = -80.0)",
    },
  },

  "VWP-WP-04": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B1 (Sensor Elevation = -17.0)",
      S3: "Water_Level_Elevation_B (Sensor Elevation = -28.5)",
      S4: "Water_Level_Elevation_C (Sensor Elevation = -63.0)",
    },
  },

  "VWP-WP-05": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B1 (Sensor Elevation = -17.0)",
      S3: "Water_Level_Elevation_B (Sensor Elevation = -28.5)",
      S4: "Water_Level_Elevation_C (Sensor Elevation = -63.0)",
    },
  },

  "VWP-WP-06": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B (Sensor Elevation = -28.5)",
      S3: "Water_Level_Elevation_C (Sensor Elevation = -63.0)",
      S4: "Water_Level_Elevation_D (Sensor Elevation = -77.0)",
    },
  },

  "VWP-WP-07": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B (Sensor Elevation = -28.5)",
      S3: "Water_Level_Elevation_C (Sensor Elevation = -67.5)",
      S4: "Water_Level_Elevation_D (Sensor Elevation = -80.0)",
    },
  },

  "VWP-WP-08": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B (Sensor Elevation = -28.5)",
      S3: "Water_Level_Elevation_C (Sensor Elevation = -63.0)",
      S4: "Water_Level_Elevation_D (Sensor Elevation = -80.0)",
    },
  },

  "VWP-WP-09": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B (Sensor Elevation = -28.5)",
      S3: "Water_Level_Elevation_C (Sensor Elevation = -63.0)",
      S4: "Water_Level_Elevation_D (Sensor Elevation = -80.0)",
    },
  },

  "VWP-WP-10": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B (Sensor Elevation = -28.5)",
      S3: "Water_Level_Elevation_C (Sensor Elevation = -63.0)",
      S4: "Water_Level_Elevation_D (Sensor Elevation = -80.0)",
    },
  },

  "VWP-WP-11": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B (Sensor Elevation = -28.5)",
      S3: "Water_Level_Elevation_C (Sensor Elevation = -63.0)",
      S4: "Water_Level_Elevation_D (Sensor Elevation = -77.0)",
    },
  },

  "VWP-WP-12": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B (Sensor Elevation = -28.5)",
      S3: "Water_Level_Elevation_C (Sensor Elevation = -63.0)",
      S4: "Water_Level_Elevation_D (Sensor Elevation = -77.0)",
    },
  },

  "VWP-WP-13": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B (Sensor Elevation = -28.5)",
      S3: "Water_Level_Elevation_C (Sensor Elevation = -63.0)",
      S4: "Water_Level_Elevation_D (Sensor Elevation = -80.0)",
    },
  },

    "VWP-WP-14": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B (Sensor Elevation = -28.5)",
      S3: "Water_Level_Elevation_C (Sensor Elevation = -63.0)",
      S4: "Water_Level_Elevation_D (Sensor Elevation = -80.0)",
    },
  },


  "VWP-WP-23": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +26.0)",
      S2: "Water_Level_Elevation_B (Sensor Elevation = -31.0)",
      S3: "Water_Level_Elevation_C (Sensor Elevation = -68.0)",
      S4: "Water_Level_Elevation_D (Sensor Elevation = -75.0)",
    },
  },

  "VWP-WP-31": {
    water_level: {
      S1: "Water_Level_Elevation_A (Sensor Elevation = +31.0)",
      S2: "Water_Level_Elevation_B (Sensor Elevation = -28.0)",
      S3: "Water_Level_Elevation_C (Sensor Elevation = -63.0)",
      S4: "Water_Level_Elevation_D (Sensor Elevation = -80.0)",
    },
  },
};

  // deviceSensorMap ke baad call hota hai: empty-data case me labels usi map se
  // aate hain, aur const hone ki wajah se pehle padhne par TDZ error milta.
  const { keyGroup: groupedData, startDate, endDate } = groupDataForCharts();

  const renderChart = (groupedKeys, title) => {
console.log('test');
console.log(groupedKeys);
console.log(title);

const sortedGroupedKeys = Object.keys(groupedKeys)
  .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  .reduce((obj, key) => {
    obj[key] = groupedKeys[key];
    return obj;
  }, {});



    if (!sortedGroupedKeys || !startDate || !endDate) return null;

    const datasets = Object.keys(sortedGroupedKeys).map((key, index) => {
      let name = key;

   if(deviceName!='VWP-WP-29' || deviceName!='VWP-WP-28' || deviceName!='VWP-WP-26' || deviceName!='VWP-WP-30' || deviceName!='VWP-WP-27') {
if (deviceSensorMap[deviceName]) {
  const sensorKey = key.split("_S")[1]; 
  console.log('sensorKey'); 
    console.log(deviceSensorMap[deviceName].water_level["S" + sensorKey]); 

  if(key.startsWith("water_level_elevation")){
  name = deviceSensorMap[deviceName].water_level["S" + sensorKey] || key;
  }
  else{

    const match = deviceSensorMap[deviceName].water_level["S" + sensorKey].match(/Water_Level_Elevation_([A-Za-z0-9]+)/);

if (match) {
  const sensorLabel = match[1]; 
    name='Pressure_in_kPa_'+sensorLabel;
}


  }
}
}

      return {
        label: name,
        data: groupedKeys[key],
        borderColor: getColor(index),
        backgroundColor: "transparent",
        fill: false,
        borderWidth:4,
        tension: 0.4,
   borderDash: [],
     pointRadius: 0,   
       pointHoverRadius: 0,  
      };
    });

    const chartData = { datasets };

const options = {
  responsive: true,

  interaction: {
    mode: "nearest",
    intersect: false,
  },

  plugins: {
    
    legend: {
      // Legend readings par nahi, labels par depend karta hai — data khali ho
      // tab bhi sensor naam dikhne chahiye. Sirf "empty" placeholder chhupta hai.
      display: datasets.some((ds) => ds.label && ds.label !== "empty"),
      position: "top",
      align: "start",
      labels: {
        usePointStyle: true,       
        pointStyle: "circle",
        padding: 12,
        
        boxWidth: 5,
        boxHeight: 5,
        generateLabels: (chart) => {
          const original = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
       return original.map((item) => {
  const label = item.text;

  const formatted =
    label.charAt(0).toUpperCase() + label.slice(1);  

  return {
    ...item,
    text: formatted,
    fillStyle: item.strokeStyle,
    strokeStyle: item.strokeStyle,
  };
});
        }
      },

  title: {
      padding: 10,  
    },
layout: {
    padding: {
      bottom: 20, 
    },
  },
    },

    title: {
      display: true,
      text: title,
      align: "center",
      font: { size: 16 },
        color: "#000", 
    },

    tooltip: {
      mode: "nearest",
      intersect: false,
    }
    
  },
 

  scales: {
    x: {
      type: "time",
      min: startDate.getTime(),
      max: endDate.getTime(),
      time: {
        unit: "day",
        displayFormats: {
          day: "dd MMM",
        },
      },

  
      grid: {
        display: false,
      },

 
      border: {
        display: false,
      },
    },

    y: {
      beginAtZero: false,

 
      border: {
        display: false,
      },
 
      grid: {
        drawBorder: false,
      }
    },
  },
};


    return (
      <div style={{ marginBottom: 50 }} key={title}>
 

     
        <Line
          height={120} 
           plugins={[legendSpacingPlugin]}
          ref={(el) => (chartRefs.current[title] = el?.chartInstance)}
          data={chartData}
          options={options}
        />
      </div>
    );
  };

return (
  <div style={{ padding: 20 }} className="graph">
    {Object.keys(groupedData)
      .sort((a, b) => {
        if (a.includes("Pressure In KPa")) return -1;
        if (b.includes("Pressure In KPa")) return 1;
        return 0;
      })
      .map((groupName) =>
        renderChart(groupedData[groupName], groupName.replace(/_/g, " "))
      )}
  </div>
);

};

export default TimeSeriesChart;
